import { useSessionState } from '@/hooks/useSessionState';
import { SIDEBAR_WIDTH } from '@/theme';
import { Box, Tooltip, styled } from '@mui/material';
import { KeyboardArrowRight } from '@mui/icons-material';
import React, { memo, useCallback, useRef, useState } from 'react';

const COLLAPSED_WIDTH = 0;
const MIN_WIDTH = 240;
const MAX_WIDTH = SIDEBAR_WIDTH * 2;
const DEFAULT_WIDTH = SIDEBAR_WIDTH;

/**
 * Dead band below the minimum width. A cursor resting near the collapse point would otherwise
 * snap the panel shut and immediately requalify it to reopen, flickering on every jitter.
 */
const REOPEN_BUFFER = 25;

/** Dragging narrower than this snaps the panel shut instead of fighting the minimum width. */
const COLLAPSE_BELOW = MIN_WIDTH - REOPEN_BUFFER;

/** Reopening lands on the minimum width, putting the panel edge back under the cursor. */
const REOPEN_ABOVE = MIN_WIDTH;

/** A press that travels less than this is a click, not a drag. */
const DRAG_THRESHOLD = 4;

const KEYBOARD_STEP = 20;

/** Collapsed, this strip is the only way back, so it is wider than the edge it is when expanded. */
const HANDLE_WIDTH = 10;
const COLLAPSED_HANDLE_WIDTH = 16;

const PANEL_WIDTH_STORAGE_KEY = 'left-panel-width';

export const PANEL_RESIZE_HANDLE_TEST_ID = 'side-panel-resize-handle';

const snapWidth = (width: number, isCollapsed: boolean) => {
  if (width < (isCollapsed ? REOPEN_ABOVE : COLLAPSE_BELOW)) return COLLAPSED_WIDTH;
  return Math.min(Math.max(width, MIN_WIDTH), MAX_WIDTH);
};

/**
 * sessionStorage is user-writable and outlives a deploy that changes these constants, so a
 * restored width is the one input that would otherwise never meet the clamp.
 */
const restoreWidth = (storedWidth: number) => {
  if (storedWidth === COLLAPSED_WIDTH) return COLLAPSED_WIDTH;
  return snapWidth(storedWidth, false);
};

const StyledSidePanel = styled(Box, {
  shouldForwardProp: property => property !== 'isCollapsed' && property !== 'isResizing',
})<{ isCollapsed: boolean; isResizing: boolean }>(({ theme, isCollapsed, isResizing }) => ({
  position: 'fixed',
  left: 0,
  height: '100%',
  backgroundColor: theme.palette.background.paper,
  zIndex: theme.zIndex.drawer,
  borderLeft: `1px solid ${theme.palette.divider}`,
  borderRight: `1px solid ${theme.palette.divider}`,
  transition: isResizing ? 'none' : 'width 0.3s ease-in-out',
  ...(isCollapsed && {
    borderLeftWidth: 0,
    borderRightWidth: 0,
    '& > *:not([data-panel-control])': {
      display: 'none',
    },
  }),
}));

const StyledResizeHandle = styled('button', {
  shouldForwardProp: property => property !== 'isCollapsed',
})<{ isCollapsed: boolean }>(({ theme, isCollapsed }) => ({
  position: 'absolute',
  top: 0,
  right: isCollapsed ? -COLLAPSED_HANDLE_WIDTH : -HANDLE_WIDTH,
  width: isCollapsed ? COLLAPSED_HANDLE_WIDTH : HANDLE_WIDTH,
  height: '100%',
  cursor: 'col-resize',
  // Without this the browser claims a touch drag as a scroll and fires pointercancel.
  touchAction: 'none',
  backgroundColor: theme.palette.background.paper,
  transition: 'background-color 0.2s ease',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  padding: 0,
  margin: 0,
  '&:hover': {
    backgroundColor: 'rgba(25, 118, 210, .5)',
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -2,
  },
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 1,
    backgroundColor: theme.palette.divider,
    transition: 'background-color 0.2s ease',
  },
  '&::before': {
    left: 0,
  },
  '&::after': {
    right: 0,
  },
}));

const ActionIcon = styled(KeyboardArrowRight, {
  shouldForwardProp: property => property !== 'isCollapsed',
})<{ isCollapsed: boolean }>(({ isCollapsed }) => ({
  fontSize: isCollapsed ? 16 : 14,
  color: 'rgba(25, 118, 210, .5)',
  transform: isCollapsed ? 'none' : 'scaleX(-1)',
}));

const ResizeHandle = memo(
  ({
    isCollapsed,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onClick,
    onKeyDown,
  }: {
    isCollapsed: boolean;
    onPointerDown: (event: React.PointerEvent) => void;
    onPointerMove: (event: React.PointerEvent) => void;
    onPointerUp: () => void;
    onPointerCancel: () => void;
    onClick: () => void;
    onKeyDown: (event: React.KeyboardEvent) => void;
  }) => {
    const label = isCollapsed ? 'Expand panel' : 'Collapse panel';

    return (
      <Tooltip title={`${label} — drag to resize`} placement='right'>
        <StyledResizeHandle
          type='button'
          isCollapsed={isCollapsed}
          data-panel-control
          aria-label={label}
          aria-expanded={!isCollapsed}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
          onClick={onClick}
          onKeyDown={onKeyDown}
          data-testid={PANEL_RESIZE_HANDLE_TEST_ID}
        >
          <ActionIcon isCollapsed={isCollapsed} />
        </StyledResizeHandle>
      </Tooltip>
    );
  }
);

ResizeHandle.displayName = 'ResizeHandle';

export const LeftPanel = ({
  children,
  className,
  style,
  onWidthChange,
  ...properties
}: {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  onWidthChange?: (width: number) => void;
} & React.ComponentProps<typeof Box>) => {
  const [storedWidth, setStoredWidth] = useSessionState(PANEL_WIDTH_STORAGE_KEY, DEFAULT_WIDTH);
  const [width, setWidth] = useState(() => restoreWidth(storedWidth));
  const [isResizing, setIsResizing] = useState(false);
  const lastExpandedWidth = useRef(storedWidth > COLLAPSED_WIDTH ? storedWidth : DEFAULT_WIDTH);
  const drag = useRef({ isActive: false, startX: 0, startWidth: 0, currentWidth: 0, hasMoved: false });

  // Settling — a released drag, a click, an arrow key. Mid-drag widths are not settled, so they
  // move the panel without persisting or waking every consumer once per pixel.
  const commitWidth = useCallback(
    (newWidth: number) => {
      setWidth(newWidth);
      setStoredWidth(newWidth);
      onWidthChange?.(newWidth);
    },
    [onWidthChange, setStoredWidth]
  );

  // Only widths the user settles on are worth restoring. Recording every width a drag passes
  // through would make a drag-to-collapse reopen at the minimum it swept past on the way down.
  const rememberExpandedWidth = (candidate: number) => {
    if (candidate > COLLAPSED_WIDTH) {
      lastExpandedWidth.current = candidate;
    }
  };

  // Pointer capture, not window listeners: the drag crosses the 3D viewer iframe, which
  // swallows every move event once the cursor leaves the panel.
  const handlePointerDown = useCallback(
    (event: React.PointerEvent) => {
      if (event.button !== 0) return;
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      rememberExpandedWidth(width);
      drag.current = {
        isActive: true,
        startX: event.clientX,
        startWidth: width,
        currentWidth: width,
        hasMoved: false,
      };
      setIsResizing(true);
    },
    [width]
  );

  const handlePointerMove = useCallback((event: React.PointerEvent) => {
    if (!drag.current.isActive) return;
    const deltaX = event.clientX - drag.current.startX;
    if (Math.abs(deltaX) > DRAG_THRESHOLD) {
      drag.current.hasMoved = true;
    }
    if (!drag.current.hasMoved) return;
    const newWidth = snapWidth(drag.current.startWidth + deltaX, drag.current.currentWidth === COLLAPSED_WIDTH);
    drag.current.currentWidth = newWidth;
    setWidth(newWidth);
  }, []);

  // The browser releases the capture itself on pointerup and pointercancel, so this only has to
  // put the panel's own state back.
  const endDrag = useCallback(
    (didComplete: boolean) => {
      if (!drag.current.isActive) return;
      drag.current.isActive = false;
      setIsResizing(false);

      // A press that never moved is a click, and the click handler owns the width. Committing
      // here too would report the unchanged width and then the toggled one for a single click.
      if (drag.current.hasMoved) {
        rememberExpandedWidth(drag.current.currentWidth);
        commitWidth(drag.current.currentWidth);
      }

      // A completed drag is followed by a click the handle must ignore; a cancelled one is not,
      // so leaving hasMoved set would swallow the user's next real click.
      drag.current.hasMoved = didComplete && drag.current.hasMoved;
    },
    [commitWidth]
  );

  const handlePointerUp = useCallback(() => endDrag(true), [endDrag]);
  const handlePointerCancel = useCallback(() => endDrag(false), [endDrag]);

  const handleClick = useCallback(() => {
    if (drag.current.hasMoved) {
      drag.current.hasMoved = false;
      return;
    }
    rememberExpandedWidth(width);
    commitWidth(width === COLLAPSED_WIDTH ? lastExpandedWidth.current : COLLAPSED_WIDTH);
  }, [commitWidth, width]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();

      // A collapsed panel can never step its way back out past the reopen buffer, so the
      // arrow key reopens it outright rather than nudging a width of zero.
      if (width === COLLAPSED_WIDTH) {
        if (event.key === 'ArrowRight') commitWidth(lastExpandedWidth.current);
        return;
      }

      const step = event.key === 'ArrowRight' ? KEYBOARD_STEP : -KEYBOARD_STEP;
      rememberExpandedWidth(width);
      commitWidth(snapWidth(width + step, false));
    },
    [commitWidth, width]
  );

  return (
    <StyledSidePanel
      isCollapsed={width === COLLAPSED_WIDTH}
      isResizing={isResizing}
      className={className}
      style={{
        width,
        ...style,
      }}
      {...properties}
    >
      {children}
      <ResizeHandle
        isCollapsed={width === COLLAPSED_WIDTH}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
      />
    </StyledSidePanel>
  );
};
